import { useEffect, useMemo, useState } from "react";
import { type Ticket } from "../../types";
import { TicketPill } from "../../components/TicketPill/TicketPill";
import { TicketPillSkeleton } from "../../components/TicketPill/TicketPillSkeleton";
import { BottomSheet } from "../../components/BottomSheet/BottomSheet";
import {
	getPaginatedAvailableTickets,
	getAvailableTicketsCount,
	markTicketsAsSold,
	PAGE_SIZE,
	type TicketsPage,
	searchTicketsByNumberWindow,
} from "../../useCases/tickets";
import { type Firestore, type QueryDocumentSnapshot } from "firebase/firestore";
import "./NewSale.scss";
import type { Analytics } from "firebase/analytics";

interface NewSaleProps {
	db: Firestore;
	analytics: Analytics;
	lottery: string;
}

export const NewSale = (props: NewSaleProps) => {
	const [availableTickets, setAvailableTickets] = useState<Ticket[]>([]);
	const [selectedTickets, setSelectedTickets] = useState<Ticket[]>([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
	const [loading, setLoading] = useState(false);

	const [page, setPage] = useState(1);
	const [cursors, setCursors] = useState<(QueryDocumentSnapshot | null)[]>([null]);
	const [lastPageHasNext, setLastPageHasNext] = useState(true);
	const [total, setTotal] = useState<number | undefined>(undefined);

	const getErrorMessage = (err: unknown) => {
		if (err instanceof Error && err.message) return err.message;
		try {
			return JSON.stringify(err);
		} catch {
			return String(err);
		}
	};

	const showErrorAlert = (context: string, err: unknown) => {
		const msg = getErrorMessage(err);
		alert(`${context}\n\nDetalle: ${msg}`);
		console.error(context, err);
	};

	const isSearching = searchQuery.trim().length > 0;

	const loadPage = async (pageIndex: number) => {
		setLoading(true);
		try {
			if (isSearching) {
				const results = await searchTicketsByNumberWindow(props.db, props.lottery, searchQuery.trim());
				setAvailableTickets(results);
				setPage(1);
				setLastPageHasNext(false);
				return;
			}

			const startCursor = cursors[pageIndex - 1] ?? null;
			const resp: TicketsPage = await getPaginatedAvailableTickets(props.db, props.lottery, startCursor);

			setAvailableTickets(resp.items);
			setLastPageHasNext(resp.hasNext);

			const newCursors = [...cursors];
			newCursors[pageIndex] = resp.lastCursor;
			setCursors(newCursors);

			setPage(pageIndex);
		} catch (err) {
			showErrorAlert("Error al cargar la página de boletos.", err);
			setAvailableTickets([]);
			setLastPageHasNext(false);
		} finally {
			setLoading(false);
		}
	};

	const refreshAvailableTickets = async () => {
		setLoading(true);
		try {
			setCursors([null]);
			await loadPage(1);
			try {
				const count = await getAvailableTicketsCount(props.db, props.lottery);
				setTotal(count);
			} catch {
			}
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		refreshAvailableTickets();
	}, [props.db]);

	useEffect(() => {
		setLoading(true);
		const handler = setTimeout(async () => {
			if (isSearching) {
				setCursors([null]);
				await loadPage(1);
			} else {
				await refreshAvailableTickets();
			}
		}, 250);
		return () => clearTimeout(handler);
	}, [props.db, searchQuery]);

	const handlePrev = async () => {
		if (page <= 1 || isSearching) return;
		await loadPage(page - 1);
	};

	const handleNext = async () => {
		if (isSearching || !lastPageHasNext) return;
		await loadPage(page + 1);
	};

	const handleTicketSelect = (ticket: Ticket) => {
		const isSelected = selectedTickets.some((t) => t.identifier === ticket.identifier);
		if (isSelected) {
			setSelectedTickets(selectedTickets.filter((t) => t.identifier !== ticket.identifier));
		} else {
			setSelectedTickets([...selectedTickets, ticket]);
		}
		setIsBottomSheetOpen(true);
	};

	const handleConfirmSale = async () => {
		try {
			await markTicketsAsSold(
				props.db,
				props.analytics,
				selectedTickets.map((t) => t.reference)
			);
			setSelectedTickets([]);
			setIsBottomSheetOpen(false);
			await refreshAvailableTickets();
		} catch (error) {
			console.error("Failed to mark tickets as sold:", error);
		}
	};

	const handleCancelSale = () => {
		setSelectedTickets([]);
		setIsBottomSheetOpen(false);
	};

	const pageInfo = useMemo(() => {
		if (isSearching) return "Resultados de búsqueda";
		const start = (page - 1) * PAGE_SIZE + 1;
		const end = start + availableTickets.length - 1;
		if (total !== undefined) {
			return `${start}-${end} de ${total}`;
		}
		return `${start}-${end}`;
	}, [isSearching, page, availableTickets.length, total]);

	return (
		<div className="new-sale">
			<div className="search-bar">
				<h2>Números disponibles</h2>
				<input
					type="text"
					placeholder="Buscar por Número..."
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
				/>
			</div>

			<div className="tickets-grid">
				{loading ? (
					Array.from({ length: PAGE_SIZE }).map((_, i) => <TicketPillSkeleton key={i} />)
				) : availableTickets.length === 0 ? (
					<div className="no-data">
						<div className="art">
							<img src={"/logo.png"} alt="logo" />
							<span>No tiene boletos asignados</span>
						</div>
					</div>
				) : (
					availableTickets.map((ticket) => (
						<TicketPill
							key={ticket.identifier}
							ticket={ticket}
							selectable
							selected={selectedTickets.some((t) => t.identifier === ticket.identifier)}
							onSelect={handleTicketSelect}
						/>
					))
				)}
			</div>

			{/* paginacion */}
			<div className="pager">
				<div className="pager-info">{pageInfo}</div>
				<div className="pager-buttons">
					<button
						className="pager-btn"
						onClick={handlePrev}
						disabled={loading || isSearching || page <= 1}
						aria-label="Página anterior"
					>
						Anterior
					</button>
					{!isSearching && <span className="pager-page">Página {page}</span>}
					<button
						className="pager-btn"
						onClick={handleNext}
						disabled={loading || isSearching || !lastPageHasNext}
						aria-label="Página siguiente"
					>
						Siguiente
					</button>
				</div>
			</div>

			<BottomSheet
				isOpen={isBottomSheetOpen && selectedTickets.length > 0}
				selectedTickets={selectedTickets}
				onConfirm={handleConfirmSale}
				onCancel={handleCancelSale}
			/>
		</div>
	);
};
